/**
 * Compact table of cached actions for a step.
 *
 * @module cache-debug/CacheDebugActionList
 */
import React from 'react';
import { Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { Warning } from '@phosphor-icons/react';
import type { CacheDebugAction } from './useCacheDebugData';
import {
  getPrimarySelectorType,
  formatSelectorValue,
  selectorTypeBadgeColor,
} from './cache-debug-helpers';

type Props = {
  actions: CacheDebugAction[];
};

export function CacheDebugActionList({ actions }: Props) {
  if (actions.length === 0) {
    return (
      <Text size="xs" className="text-muted-foreground italic pl-2">
        No cached actions
      </Text>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-gray-200 dark:border-gray-700">
            <th className="py-1 px-2 w-8">#</th>
            <th className="py-1 px-2">Action</th>
            <th className="py-1 px-2">Element</th>
            <th className="py-1 px-2">Selector</th>
            <th className="py-1 px-2 w-24">Author</th>
          </tr>
        </thead>
        <tbody>
          {actions.map((action) => {
            const selectorType = getPrimarySelectorType(action.selectors);
            const selectorDisplay = formatSelectorValue(action.selectors);
            const badgeColor = selectorType
              ? selectorTypeBadgeColor[selectorType] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              : '';

            return (
              <tr
                key={action.index}
                className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <td className="py-1 px-2 text-muted-foreground tabular-nums">{action.index}</td>
                <td className="py-1 px-2 font-mono">{action.actionType}</td>
                <td className="py-1 px-2 max-w-[200px] truncate" title={action.elementDescription ?? undefined}>
                  {action.elementDescription ?? '—'}
                </td>
                <td className="py-1 px-2">
                  {selectorType ? (
                    <span className="inline-flex items-center gap-1">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${badgeColor}`}>
                        {selectorType}
                      </span>
                      <span className="font-mono text-[11px] max-w-[180px] truncate" title={selectorDisplay}>
                        {selectorDisplay}
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-1 px-2">
                  <span className="inline-flex items-center gap-1">
                    <AuthorBadge author={action.selectorAuthor} />
                    {action.requiresAgent && (
                      <TooltipTrigger>
                        <Warning size={12} className="text-amber-500" />
                        <Tooltip>Requires agent re-execution</Tooltip>
                      </TooltipTrigger>
                    )}
                    {action.selectorAgentFailureReason && (
                      <TooltipTrigger>
                        <Warning size={12} className="text-red-400" />
                        <Tooltip>{action.selectorAgentFailureReason}</Tooltip>
                      </TooltipTrigger>
                    )}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AuthorBadge({ author }: { author: 'deterministic' | 'agentic' | null }) {
  if (author === 'agentic') {
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
        AI Locator
      </span>
    );
  }
  if (author === 'deterministic') {
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
        Heuristic
      </span>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}
